import React, { useCallback, useMemo } from 'react';
import SVGIcon from '../../../assets/images/svg/SVGIcon';
import { Default, RightRow, StatusRow, Status } from './styles';
import { Autotext, Box, LabelTitle, LeftRow, Row, Subtitle, Title } from '../styles';
import Button from '../../button';
import { Dropdown, DropdownItem } from '../../dropdown';
import { PrioritiesList } from '../../../global/row';
import { updateProjectData } from '../../../services/projectServices';
import { rootStore } from '../../../mobx/rootStore';
import { observer } from 'mobx-react-lite';
import { BUTTON_TYPE, ICON_BUTTON_SIZE, ICON_BUTTON_TYPE, ICON_SIZE } from '../../../global/constants';
import IconButton from '../../button/iconButton';
import SwitchInput from '../../input/switchInput';

interface Props {
  themeMode: { theme: string };
  onUpdatePriority: (value: boolean) => void;
  loadingButton: boolean;
  isTemplate?: boolean;
}

const PrioritySettingCard: React.FC<Props> = (props) => {
  const { onUpdatePriority, loadingButton, isTemplate = false } = props;

  //Mobx store variables
  const { projectStore, projectTemplateStore } = rootStore;
  const { currentProject: projectDetails } = projectStore;
  const { currentProjectTemplate } = projectTemplateStore;
  const currentProject = isTemplate ? currentProjectTemplate : projectDetails;

  // for set default priority
  const onChangeDefault = useCallback(
    async (key: number) => {
      if (isTemplate) {
        rootStore.projectTemplateStore.currentProjectTemplate?.setDefaultPriority(key);
      } else {
        const payload = {
          Priority: {
            Enabled: currentProject?.priority?.enabled,
            Default: key
          }
        };
        rootStore.projectStore.currentProject?.setDefaultPriority(key);
        await updateProjectData(currentProject?.id, payload);
      }
    },
    [currentProject, isTemplate]
  );

  // Memoize the rendered list of priorities
  const priorityElements = useMemo(() => {
    return PrioritiesList.map((priority) => (
      <StatusRow key={priority.key}>
        <RightRow>
          <SVGIcon
            name={priority.icon}
            width={ICON_SIZE.MD}
            height={ICON_SIZE.MD}
            viewBox={`0 0 ${ICON_SIZE.MD} ${ICON_SIZE.MD}`}
          />
          <LabelTitle>{priority.title}</LabelTitle>
        </RightRow>
        {/* set priority as default / show more icon */}
        {currentProject?.priority?.default === priority.key ? (
          <Default>Default</Default>
        ) : (
          <Dropdown
            modalTitle='Priority'
            activeClassName='active-state'
            content={
              <IconButton
                iconName='three-dots-horizontal-icon'
                size={ICON_BUTTON_SIZE.SMALL}
                variant={ICON_BUTTON_TYPE.TERTIARY}
              />
            }
            trigger='click'>
            <DropdownItem
              onClick={() => {
                if (priority.key !== undefined) {
                  onChangeDefault(priority.key);
                }
              }}
              label='Set as Default'
            />
          </Dropdown>
        )}
      </StatusRow>
    ));
  }, [currentProject?.priority?.default, onChangeDefault]);

  return (
    <Box>
      <Row>
        <LeftRow>
          <Title>Priorities</Title>
          <Subtitle>
            Priorities communicate and prioritize tasks. Issues are linearly ordered by default, but can be changed in
            different views.
          </Subtitle>
        </LeftRow>
        <Autotext>
          {loadingButton ? (
            <Button variant={BUTTON_TYPE.PRIMARY} isLoading={loadingButton} />
          ) : (
            <SwitchInput
              checked={currentProject?.priority?.enabled || false}
              onChange={() => onUpdatePriority(!currentProject?.priority?.enabled)}
            />
          )}
        </Autotext>
      </Row>
      {currentProject?.priority?.enabled === true && <Status>{priorityElements}</Status>}
    </Box>
  );
};

export default observer(PrioritySettingCard);
